import en from './en.json';
import ru from './ru.json';
import es from './es.json';
import pt from './pt.json';
import tr from './tr.json';
import zh from './zh.json';
import type { Bundle, Locale, TranslationKey } from './types';
import { AVAILABLE_LOCALES, DEFAULT_LOCALE, PREFIX_ALTERNATION } from './types';
import { catalogGlobals } from '@roamzy/shared';
import { allCountries } from '../data/prices';

/**
 * Figures any string may quote ({min_topup}, {countries}, {floor_gb}, {year}),
 * filled in by t() and applyTranslations() alike, and by the server the same
 * way (shared catalog-globals.ts).
 */
const GLOBAL_VARS: Record<string, string> = catalogGlobals(allCountries);

export type { Locale, TranslationKey } from './types';
export { AVAILABLE_LOCALES, DEFAULT_LOCALE } from './types';

// Bundles need a `Bundle`-compatible cast — _meta keys differ between en (no
// _meta) and the others. Treated as the same shape for `t()` purposes.
const bundles: Record<Locale, Bundle> = {
  en,
  ru: ru as unknown as Bundle,
  es: es as unknown as Bundle,
  pt: pt as unknown as Bundle,
  tr: tr as unknown as Bundle,
  zh: zh as unknown as Bundle,
};

export const availableLocales: readonly Locale[] = AVAILABLE_LOCALES;

/**
 * Read locale from URL path prefix: `/ru/...` → 'ru'. Bare `/foo` → 'en'.
 * Used by main.ts at boot to set the initial locale before Router.start().
 */
export function localeFromPath(pathname: string): Locale {
  const m = pathname.match(new RegExp(`^/(${PREFIX_ALTERNATION})(?:/|$)`));
  return (m?.[1] as Locale | undefined) ?? DEFAULT_LOCALE;
}

/**
 * Strip the locale prefix from a path so route matching still hits the
 * canonical English route table. `/ru/prices` → `/prices`.
 */
export function stripLocale(pathname: string): string {
  return pathname.replace(new RegExp(`^/(?:${PREFIX_ALTERNATION})(?=/|$)`), '') || '/';
}

/**
 * Prepend the active locale prefix to a canonical path.
 * `/prices` + locale 'ru' → `/ru/prices`. English never gets a prefix.
 */
export function withLocale(pathname: string, locale: Locale = activeLocale): string {
  const stripped = stripLocale(pathname);
  if (locale === 'en') return stripped;
  return `/${locale}${stripped === '/' ? '' : stripped}`;
}

let activeLocale: Locale = 'en';
let activeBundle: Bundle = bundles[activeLocale];

export function getLocale(): Locale {
  return activeLocale;
}

export function setLocale(locale: Locale): void {
  const bundle = bundles[locale];
  if (!bundle) {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] unknown locale "${locale}", staying on "${activeLocale}"`);
    }
    return;
  }
  activeLocale = locale;
  activeBundle = bundle;
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
    applyTranslations();
  }
}

/**
 * Walks the document for `[data-i18n="some.key"]` elements and replaces their
 * text content with the translated value. Used to translate the static chrome
 * (nav, footer, cookie banner, mobile menu) which lives in `index.html` and
 * isn't rendered by the route system.
 *
 * Re-runs on every `setLocale()` call. Idempotent — safe to call repeatedly.
 *
 * Anchor tags inside translated nodes (like the cookie-policy link) are
 * preserved because we only set textContent on simple-text elements; for
 * mixed content, the bundle key holds plain text and the surrounding HTML
 * stays as-is. Today no key needs mixed-content handling.
 */
// Auto-interpolated vars available to every applyTranslations() pass. Keeps
// bundle strings like "© {year} Roamzy" working without per-element wiring.
function staticChromeVars(): Record<string, string | number> {
  return GLOBAL_VARS;
}

export function applyTranslations(root: Document | HTMLElement = document): void {
  if (typeof root.querySelectorAll !== 'function') return;
  const nodes = root.querySelectorAll<HTMLElement>('[data-i18n]');
  const vars = staticChromeVars();
  nodes.forEach((el) => {
    const key = el.dataset.i18n;
    if (!key) return;
    // Same EN-fallback contract as t() — translations applied to static chrome
    // also fall back to EN when the active locale is missing the key.
    const value = resolve(activeBundle, key) ?? (activeLocale !== 'en' ? resolve(bundles.en, key) : undefined);
    if (value === undefined) return;
    const interpolated = interpolate(value, vars);
    // Some translation values contain inline markup (<strong>, <a>). Detect
    // those and use innerHTML so it renders. Safe because translations are
    // bundled at build time from the repo's `i18n/*.json` files (no user input).
    if (interpolated.indexOf('<') !== -1) {
      el.innerHTML = interpolated;
    } else {
      el.textContent = interpolated;
    }
  });
  // Also update the language code badge in nav AND the aria-label of
  // its parent button — operator's Lighthouse pass 2026-05-09 caught
  // an a11y mismatch (visible text "EN" wasn't part of the accessible
  // name when aria-label="Choose language" overrode it). Including the
  // active locale name in aria-label fixes the mismatch.
  const localeNames: Record<string, string> = {
    en: 'English', ru: 'Russian', es: 'Spanish', pt: 'Portuguese', tr: 'Turkish',
  };
  const codeEl = root.querySelector?.('[data-lang-current]');
  if (codeEl) codeEl.textContent = activeLocale.toUpperCase();
  const langBtn = root.querySelector?.<HTMLButtonElement>('button.nav__lang');
  if (langBtn) {
    langBtn.setAttribute(
      'aria-label',
      `Language: ${localeNames[activeLocale] ?? activeLocale.toUpperCase()}. Choose language.`,
    );
  }
  // Locale-aware launch attribution link in the footer ("Made by 1 person + AI").
  // Each locale has its own static /launch-XX.html page; we swap the href so the
  // user lands on the launch story in their current language. Updated on every
  // setLocale() call alongside the rest of the chrome.
  const launchHrefByLocale: Record<string, string> = {
    ru: '/launch.html',
    en: '/launch-en.html',
    es: '/launch-es.html',
    pt: '/launch-pt.html',
    tr: '/launch-tr.html',
    zh: '/launch-zh.html',
  };
  const launchHref = launchHrefByLocale[activeLocale] ?? '/launch-en.html';
  root.querySelectorAll?.<HTMLAnchorElement>('[data-launch-link]').forEach((el) => {
    el.href = launchHref;
  });
}

function resolve(bundle: Bundle, key: string): string | undefined {
  let node: unknown = bundle;
  for (const part of key.split('.')) {
    if (node && typeof node === 'object' && part in (node as Record<string, unknown>)) {
      node = (node as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return typeof node === 'string' ? node : undefined;
}

function interpolate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

/**
 * Structured (non-string) lookup, with the same EN fallback contract as t().
 *
 * `t()` returns the key itself when a value is not a string, which is right
 * for copy but useless for the parts of the catalog that are data — the guide
 * reference sections are an array of objects. Callers own the shape.
 */
export function tData<T>(key: string): T | undefined {
  // resolve() deliberately returns undefined for non-string nodes, so this
  // walks the bundle itself rather than reusing it.
  const walk = (bundle: Bundle): unknown => {
    let node: unknown = bundle;
    for (const part of key.split('.')) {
      if (node && typeof node === 'object' && part in (node as Record<string, unknown>)) {
        node = (node as Record<string, unknown>)[part];
      } else {
        return undefined;
      }
    }
    return node;
  };
  const value = walk(activeBundle) ?? (activeLocale !== 'en' ? walk(bundles.en) : undefined);
  return value as T | undefined;
}

export function t(key: TranslationKey, vars?: Record<string, string | number>): string {
  let value = resolve(activeBundle, key);
  // EN fallback: if the active locale is missing the key but EN has it,
  // render the EN value rather than the raw dotted-path. Production users
  // never see a leaked key. Dev gets a console.warn so we know to backfill.
  if (value === undefined && activeLocale !== 'en') {
    const fallback = resolve(bundles.en, key);
    if (fallback !== undefined) {
      if (import.meta.env.DEV) {
        console.warn(`[i18n] key "${key}" missing in "${activeLocale}" — falling back to EN`);
      }
      value = fallback;
    }
  }
  if (value === undefined) {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] missing key "${key}" in EN — fix the source bundle.`);
    }
    return key;
  }
  return interpolate(value, vars ? { ...GLOBAL_VARS, ...vars } : GLOBAL_VARS);
}
